import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styles from "./BookEpisodeReader.module.css";
import { ArrowLeft, Settings, ChevronLeft, ChevronRight } from "lucide-react";
import { useEbookBySlug } from "../../hooks/useEbooksHooks";
import { useSubscriptionCheck } from "../../hooks/useSubscriptionCheck";
import { Helmet } from "react-helmet";

// Mobile detection hook
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isMobile;
};

// Pagination helpers (simplified to word limit per page like BookPreview)
const countWords = (text) => text.trim().split(/\s+/).filter(word => word.length > 0).length;
const computeWordsPerPage = (fontSize, isMobile) => {
  const baseAt18 = isMobile ? 170 : 240;
  const scale = 18 / Math.max(12, Math.min(28, fontSize || 18));
  const estimated = Math.round(baseAt18 * scale);
  return Math.max(90, Math.min(500, estimated));
};

function splitContentIntoPages(htmlContent, wordsPerPage = 250) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${htmlContent}</div>`, 'text/html');
  const container = doc.querySelector('div');
  const pages = [];
  let currentPage = document.createElement('div');
  let currentWordCount = 0;

  const commitPageIfNeeded = () => {
    if (currentPage.innerHTML.trim()) {
      pages.push({ htmlContent: currentPage.innerHTML, wordCount: currentWordCount });
      currentPage = document.createElement('div');
      currentWordCount = 0;
    }
  };

  Array.from(container.childNodes).forEach(node => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      if (!text.trim()) return;
      const words = text.trim().split(/\s+/).filter(Boolean);
      let idx = 0;
      while (idx < words.length) {
        const remaining = wordsPerPage - currentWordCount;
        if (remaining <= 0) {
          commitPageIfNeeded();
          continue;
        }
        const take = Math.min(remaining, words.length - idx);
        const chunk = words.slice(idx, idx + take).join(' ');
        currentPage.appendChild(document.createTextNode((currentWordCount > 0 ? ' ' : '') + chunk));
        currentWordCount += take;
        idx += take;
        if (currentWordCount >= wordsPerPage) {
          commitPageIfNeeded();
        }
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName;
      if (['P','H1','H2','H3','H4','H5','H6','LI','DIV','BLOCKQUOTE'].includes(tag)) {
        const text = node.textContent || '';
        const totalWords = countWords(text);
        if (totalWords === 0) {
          currentPage.appendChild(node.cloneNode(true));
          return;
        }
        if (currentWordCount + totalWords <= wordsPerPage) {
          currentPage.appendChild(node.cloneNode(true));
          currentWordCount += totalWords;
        } else {
          let remainingWordsInPage = wordsPerPage - currentWordCount;
          const words = (text || '').trim().split(/\s+/).filter(Boolean);
          let start = 0;
          while (start < words.length) {
            if (remainingWordsInPage <= 0) {
              commitPageIfNeeded();
              remainingWordsInPage = wordsPerPage;
            }
            const take = Math.min(remainingWordsInPage, words.length - start);
            const chunk = words.slice(start, start + take).join(' ');
            const newEl = document.createElement(tag);
            newEl.textContent = chunk;
            currentPage.appendChild(newEl);
            currentWordCount += take;
            start += take;
            remainingWordsInPage = wordsPerPage - currentWordCount;
            if (currentWordCount >= wordsPerPage) {
              commitPageIfNeeded();
            }
          }
        }
      } else if (['UL','OL','TABLE','IMG','PRE','CODE','HR'].includes(tag)) {
        if (currentWordCount >= wordsPerPage) {
          commitPageIfNeeded();
        }
        currentPage.appendChild(node.cloneNode(true));
      } else {
        currentPage.appendChild(node.cloneNode(true));
      }
    }
  });

  commitPageIfNeeded();
  return pages;
}

const EbookReader = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { ebook, isLoading, error } = useEbookBySlug(slug);
  const { checkSubscriptionBeforePlay } = useSubscriptionCheck();
  const isMobile = useIsMobile();

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [fontSize, setFontSize] = useState(isMobile ? 14 : 18);
  const [pageColor, setPageColor] = useState("#f8f5e9"); // Default cream color
  const [textColor, setTextColor] = useState("#333333"); // Default dark gray
  const [showSettings, setShowSettings] = useState(false);
  // Pages derived via memo based on content and font size
  const [showPageJump, setShowPageJump] = useState(false);
  const [jumpPageNumber, setJumpPageNumber] = useState("");
  const [currentTexture, setCurrentTexture] = useState(false);

  const contentRef = useRef(null);

  // Check authentication and subscription when component loads
  useEffect(() => {
    // Check subscription before allowing access to the reader
    if (!checkSubscriptionBeforePlay()) {
      return;
    }
  }, [checkSubscriptionBeforePlay]);

  // Colors for theme options
  const colorThemes = [
    { bg: "#f8f5e9", text: "#333333", name: "Cream" },
    { bg: "#ffffff", text: "#333333", name: "White" },
    { bg: "#f0f0f0", text: "#333333", name: "Light Gray" },
    { bg: "#1a1a1a", text: "#e0e0e0", name: "Dark" },
    { bg: "#2d2d2d", text: "#d4c7a5", name: "Sepia Dark" },
    { bg: "#F3F3F3", text: "#2c2c2c", name: "Textured Paper", texture: true },
  ];

  // Update fontSize when device changes
  useEffect(() => {
    setFontSize(isMobile ? 14 : 18);
  }, [isMobile]);

  // Compute pages like BookPreview: word-limit per page, simplified logic
  const pages = useMemo(() => {
    if (isLoading || error || !ebook?.full_content) return [];
    const wpp = computeWordsPerPage(fontSize, isMobile);
    const contentPages = splitContentIntoPages(ebook.full_content, wpp);
    setTotalPages(contentPages.length);
    return contentPages;
  }, [ebook, isLoading, error, isMobile, fontSize]);

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
      window.scrollTo(0, 0);
    }
  };

  const toggleSettings = () => {
    setShowSettings(!showSettings);
  };

  const applyColorTheme = (bg, text, texture = false) => {
    setPageColor(bg);
    setTextColor(text);
    setCurrentTexture(texture);
    
    // Remove any existing texture styles from body
    document.body.style.cssText = '';
  };

  const changeFontSize = (delta) => {
    const newSize = fontSize + delta;
    if (newSize >= 14 && newSize <= 24) {
      setFontSize(newSize);
    }
  };

  const handlePageJump = () => {
    const pageNum = parseInt(jumpPageNumber);
    if (pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum - 1);
      setJumpPageNumber("");
      setShowPageJump(false);
      window.scrollTo(0, 0);
    }
  };

  const handlePageJumpInputChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setJumpPageNumber(value);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") {
        if (currentPage < totalPages - 1) {
          setCurrentPage((p) => p + 1);
          window.scrollTo(0, 0);
        }
      } else if (e.key === "ArrowLeft") {
        if (currentPage > 0) {
          setCurrentPage((p) => p - 1);
          window.scrollTo(0, 0);
        }
      } else if (e.key === "Escape") {
        setShowSettings(false);
        setShowPageJump(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      // Clean up texture when component unmounts
      document.body.style.cssText = '';
    };
  }, [currentPage, totalPages]);

  if (isLoading) {
    return (
      <div className={styles.loaderContainer}>
        <div className={styles.loader}></div>
        <p>Loading ebook...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <h2>Error loading ebook</h2>
        <p>{error.message || "Something went wrong"}</p>
        <button onClick={() => navigate(-1)} className={styles.backButton}>
          <ArrowLeft size={20} /> Go Back
        </button>
      </div>
    );
  }

  if (!ebook) {
    return (
      <div className={styles.errorContainer}>
        <h2>Ebook not found</h2>
        <button onClick={() => navigate(-1)} className={styles.backButton}>
          <ArrowLeft size={20} /> Go Back
        </button>
      </div>
    );
  }

  return (
    <>
    <Helmet>
      <title>{ebook.title} - Ebook Reader</title>
      <meta name="description" content={`Read ${ebook.title} by ${ebook.author || ebook.creator_name} on SunoKahaniyan.`} />
    </Helmet>
    <div
      className={styles.readerContainer}
      style={{ backgroundColor: pageColor }}
    >
               <div
           className={styles.header}
           style={{ 
             backgroundColor: pageColor, 
             color: textColor,
             ...(currentTexture && {
               backgroundImage: `url('/texture/graytexture.webp')`,
               backgroundSize: '200px 200px',
               backgroundRepeat: 'repeat',
               backgroundPosition: '0 0',
               backgroundAttachment: 'local'
             })
           }}
         >
        <button
          onClick={() => navigate(-1)}
          className={styles.backButton}
          style={{ color: textColor }}
        >
          <ArrowLeft size={20} />
        </button>
        <div className={styles.bookInfo} style={{ color: textColor }}>
          <h1 style={{ color: textColor }}>{ebook.title}</h1>
          <p style={{ color: textColor, opacity: 0.7 }}>
            by {ebook.author || ebook.creator_name}
          </p>
        </div>
        <button
          onClick={toggleSettings}
          className={styles.settingsButton}
          style={{ color: textColor }}
        >
          <Settings size={20} />
        </button>
      </div>

      {/* Settings Panel */}
      {showSettings && (
                 <div
           className={styles.settingsPanel}
           style={{ 
             backgroundColor: pageColor, 
             color: textColor,
             ...(currentTexture && {
               backgroundImage: `url('/texture/graytexture.webp')`,
               backgroundSize: '200px 200px',
               backgroundRepeat: 'repeat',
               backgroundPosition: '0 0',
               backgroundAttachment: 'local'
             })
           }}
         >
          {/* Close Button */}
          <button
            className={styles.closeButton}
            onClick={() => setShowSettings(false)}
            style={{ color: textColor }}
          >
            ×
          </button>

          <h2 className={styles.settingsTitle} style={{ color: textColor }}>
            Themes & Settings
          </h2>

          {/* Font Size Section */}
          <div className={styles.settingsSection}>
            <div className={styles.fontSizeControls}>
              <button
                onClick={() => changeFontSize(-1)}
                disabled={fontSize <= 14}
                className={styles.fontSizeButton}
                style={{
                  borderColor: textColor,
                  color: textColor,
                  backgroundColor: "transparent",
                }}
              >
                A
              </button>
              <div
                className={styles.fontSizeDivider}
                style={{ backgroundColor: textColor }}
              ></div>
              <button
                onClick={() => changeFontSize(1)}
                disabled={fontSize >= 24}
                className={styles.fontSizeButton}
                style={{
                  borderColor: textColor,
                  color: textColor,
                  backgroundColor: "transparent",
                }}
              >
                A
              </button>
            </div>
          </div>

          {/* Theme Options */}
          <div className={styles.settingsSection}>
            <div className={styles.themeGrid}>
              {colorThemes.map((theme, index) => (
                <button
                  key={index}
                  className={`${styles.themeCard} ${
                    theme.bg === pageColor && theme.text === textColor
                      ? styles.activeTheme
                      : ""
                  }`}
                  style={{
                    backgroundColor: theme.bg,
                    border:
                      theme.bg === pageColor && theme.text === textColor
                        ? `2px solid ${textColor}`
                        : `1px solid ${theme.text}20`,
                  }}
                  onClick={() => applyColorTheme(theme.bg, theme.text, theme.texture)}
                >
                  <div className={styles.themePreview}>
                    <span
                      className={styles.themeText}
                      style={{ color: theme.text }}
                    >
                      {theme.texture ? "📄" : "Aa"}
                    </span>
                  </div>
                  <span
                    className={styles.themeName}
                    style={{ color: theme.text }}
                  >
                    {theme.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Page Jump Modal */}
      {showPageJump && (
        <div className={styles.pageJumpOverlay}>
                     <div
             className={styles.pageJumpModal}
             style={{ 
               backgroundColor: pageColor, 
               color: textColor,
               ...(currentTexture && {
                 backgroundImage: `url('/texture/graytexture.webp')`,
                 backgroundSize: '200px 200px',
                 backgroundRepeat: 'repeat',
                 backgroundPosition: '0 0',
                 backgroundAttachment: 'local'
               })
             }}
           >
            <h3 style={{ color: textColor }}>Go to Page</h3>
            <div className={styles.pageJumpControls}>
              <input
                type="text"
                value={jumpPageNumber}
                onChange={handlePageJumpInputChange}
                placeholder={`1-${totalPages}`}
                className={styles.pageJumpInput}
                style={{
                  backgroundColor: "transparent",
                  color: textColor,
                  borderColor: textColor,
                }}
                onKeyPress={(e) => e.key === "Enter" && handlePageJump()}
                autoFocus
              />
              <button
                onClick={handlePageJump}
                className={styles.pageJumpButton}
                style={{
                  backgroundColor: textColor,
                  color: pageColor,
                }}
                disabled={
                  !jumpPageNumber ||
                  parseInt(jumpPageNumber) < 1 ||
                  parseInt(jumpPageNumber) > totalPages
                }
              >
                Go
              </button>
            </div>
            <button
              onClick={() => setShowPageJump(false)}
              className={styles.pageJumpCancel}
              style={{ color: textColor }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Content Area */}
           <div
  className={styles.contentArea}
  ref={contentRef}
  style={{
    fontSize: `${fontSize}px`,
    color: textColor,
    backgroundColor: pageColor,
         ...(currentTexture && {
       backgroundImage: `url('/texture/graytexture.webp')`,
       backgroundSize: '200px 200px',
       backgroundRepeat: 'repeat',
       backgroundPosition: '0 0',
       backgroundAttachment: 'local'
     })
  }}
>
  {pages.length > 0 ? (
    <div className={styles.pageContent}>
      {currentPage === 0 && (
        <div className={styles.bookHeader}>
          <h1 className={styles.bookTitle} style={{ color: textColor }}>
            {ebook.title}
          </h1>
          <h2
            className={styles.bookAuthor}
            style={{ color: textColor, opacity: 0.8 }}
          >
            by {ebook.author || ebook.creator_name}
          </h2>
          <div
            className={styles.titleSeparator}
            style={{ backgroundColor: textColor, opacity: 0.3 }}
          ></div>
        </div>
      )}
      {/* Add CSS for proper table rendering */}
      <style jsx>{`
        .pageContent table {
          border-collapse: collapse;
          width: 100%;
          margin: 1rem 0;
          border: 1px solid ${textColor}40;
        }
        .pageContent td, .pageContent th {
          border: 1px solid ${textColor}40;
          padding: 8px 12px;
          text-align: left;
        }
        .pageContent th {
          background-color: ${textColor}10;
          font-weight: bold;
        }
        .pageContent p {
          margin: 0.5rem 0;
          line-height: 1.6;
        }
        .pageContent em {
          font-style: italic;
        }
        .pageContent strong {
          font-weight: bold;
        }
      `}</style>
      {/* Use dangerouslySetInnerHTML for better HTML rendering */}
      <div
        className="pageContent"
        dangerouslySetInnerHTML={{
          __html: pages[currentPage]?.htmlContent || "",
        }}
      />
    </div>
  ) : (
    <div
      className={styles.pageContent}
      dangerouslySetInnerHTML={{ __html: ebook.full_content }}
    />
  )}
</div>

      {/* Pagination Controls */}
             <div
         className={styles.paginationControls}
         style={{ 
           backgroundColor: pageColor, 
           borderColor: `${textColor}20`,
           ...(currentTexture && {
             backgroundImage: `url('/texture/graytexture.webp')`,
             backgroundSize: '200px 200px',
             backgroundRepeat: 'repeat',
             backgroundPosition: '0 0',
             backgroundAttachment: 'local'
           })
         }}
       >
        <button
          onClick={handlePrevPage}
          disabled={currentPage === 0}
          className={styles.pageButton}
          style={{ color: textColor }}
          aria-label="Previous page"
        >
          <ChevronLeft size={24} />
        </button>
        <div className={styles.pageInfo} style={{ color: textColor }}>
          <button
            onClick={() => setShowPageJump(true)}
            className={styles.pageInfoButton}
            style={{ color: textColor }}
          >
            Page {currentPage + 1} of {totalPages || 1}
          </button>
        </div>
        <button
          onClick={handleNextPage}
          disabled={currentPage >= totalPages - 1}
          className={styles.pageButton}
          style={{ color: textColor }}
          aria-label="Next page"
        >
          <ChevronRight size={24} />
        </button>
      </div>
    </div>
    </>
  );
};

export default EbookReader;